/**
 * Test-only entry point for the browser-driven FlashBook layout tests (see
 * `e2e-web/`).
 *
 * Registered only in a dev build (see `IS_DEV`), and deliberately the one
 * FlashBook route *not* behind `requireAuth`: the tests need to measure the real
 * studio cards and the real player, and a fresh browser has no session to sign
 * in with. Everything it renders is the shipping component — only the book it
 * renders is seeded.
 *
 *   /flashbook-fixtures?fixture=studio&themeId=magazine
 *     the real studio, seeded with the layout fixture book
 *   /flashbook-fixtures?fixture=player&themeId=magazine
 *     the real player, reading that book, with a block switcher so each block
 *     type can be measured without reloading the app
 *   /flashbook-fixtures?fixture=player&block=find_words
 *     the player reading a one-block book
 *   /flashbook-fixtures?fixture=player&lang=ar&themeId=notebook
 *     the player reading a book written right-to-left
 *   /flashbook-fixtures?fixture=capsule
 *     the real Capsule studio, so its chrome (header, block picker, slide bar,
 *     delete confirmation) can be measured in both appearances
 *   /flashbook-fixtures?fixture=studio&session=1&step=cover
 *     the real studio signed in as a stand-in creator, so the flows that need an
 *     agent — the drafts list opening one of them — are reachable in a browser
 *   /flashbook-fixtures?fixture=post
 *     the real feed row for a published book, so a browser can see what a
 *     FlashBook looks like in a timeline — and open it
 *   /flashbook-fixtures?fixture=post&noRef=1
 *     the same post without the publisher's back-ref, which is the post whose
 *     book has to be found through its author
 */
import {useMemo, useRef, useState} from 'react'
import {ScrollView, Text, TouchableOpacity, View} from 'react-native'
import {
  type AppBskyFeedDefs,
  type AppBskyFeedPost,
  type AtpAgent,
  moderatePost,
  type ModerationOpts,
} from '@atproto/api'
import {useNavigation, useRoute} from '@react-navigation/native'

import {
  buildArabicFixture,
  buildClassPlays,
  buildFlashBookFixture,
  buildProgressSeed,
  buildRemoteReadSeed,
  buildReportSeed,
  buildShelfRead,
  buildSingleBlockFixture,
  buildStressFixture,
  FIXTURE_BLOCK_SPECS,
  FIXTURE_COVER_POSTER_URI,
  FIXTURE_UNREADABLE_AUDIO,
  type FixtureBlockSpec,
  withFilmCover,
  withLevelledReadings,
  withReadingAudio,
} from '#/lib/flashbookFixtures'
import {putFlashBookProgress} from '#/lib/flashbookProgressStore'
import {
  PROGRESS_COLLECTION,
  progressRecordFor,
} from '#/lib/flashbookProgressSync'
import {flashbookPostFacts, flashbookPostRecord} from '#/lib/flashbookPublish'
import {moderationOptsOverrideContext} from '#/state/preferences/moderation-opts'
import {AgentContext} from '#/state/session'
import {PostFeedItem} from '#/view/com/posts/PostFeedItem'
import {CapsuleStudioScreen} from '#/screens/CapsuleStudio'
import {FlashBookStudioScreen} from '#/screens/FlashBookStudio'
import {FlashBookPlayer} from '#/components/flashbooks/FlashBookPlayer'
import {countExercises} from '#/components/flashbooks/flashbookSave'
import {FlashBookShelf} from '#/components/flashbooks/FlashBookShelf'
import * as Layout from '#/components/Layout'
import {useStudioChrome} from '#/themes-studio/useChrome'
import {type FlashBookModel} from '#/types/flashbook'

type FixtureParams = {
  fixture: 'studio' | 'player' | 'capsule' | 'post' | 'shelf'
  themeId?: string
  /**
   * Open the reader on its report, over a seeded read of this book.
   *
   * The ending is the one screen a browser cannot reach the honest way — it would
   * have to answer ten different question widgets first — and it is the screen a
   * learner is left with, so it is worth being able to measure on every theme.
   */
  report: boolean
  /** Which page the player should open on, so each page can be measured. */
  pageIndex: number
  /**
   * Measure just this one block, in a book of its own. A plain slide type, or a
   * rich reading block addressed as `content:quote` / `content:table` / …
   */
  block?: FixtureBlockSpec
  /**
   * Read the *stress* book instead: Arabic, French and pathologically long
   * content, so a theme's layout is measured against content that fights back.
   */
  stress: boolean
  /**
   * Open the reader on a book somebody has been reading for a week.
   *
   * The resume screen is only reachable from a *history* — reads that ended and a
   * read still open — which a browser cannot produce without answering a book's
   * worth of questions across several sittings first. So the reader is opened on a
   * seeded history and, unlike every other fixture here, is allowed to *keep*
   * recording: what it writes after a continue is what makes "close the book and
   * come back" a claim the browser can actually test.
   */
  progress: boolean
  /**
   * Open the reader at the learner's *place* rather than on the cover.
   *
   * The other half of a way into a book: a feed card with Continue on it promises a
   * page, and the promise is kept by the reader. What the browser has to be able to
   * measure is therefore not the tap — that is a route, and the card's own suite
   * reads it off the URL — but what the reader does with it, which needs a place to
   * exist first. Pair with `progress`, which seeds one.
   */
  resume: boolean
  /**
   * Play the book as its *author*, with a class's worth of plays behind it.
   *
   * The teacher's ending needs two things a browser cannot produce: a session that
   * matches the book's author, and other learners' results — which live on an
   * AppView this suite does not have and cannot post to. So the author flag is
   * passed and the plays are handed straight to the screen, and what gets measured
   * is the class report that ships rather than a mock of it. Pair with `report` to
   * land on the report the door opens from.
   */
  classPlays: boolean
  /**
   * Ask the reader for the *class report* rather than for the book.
   *
   * The teacher's other way in: a feed card sends this param, and what the browser
   * has to be able to measure is what the reader does with it — which is two things,
   * because the route is public. With `classPlays` (the author) the reader opens on
   * the class report; without it (anybody else) the request is refused and the book
   * opens as it always would. Two flags that must agree, so both halves be gated.
   */
  classReport: boolean
  /**
   * Look at the feed as somebody who is *not* the book's author — but is signed in.
   *
   * The negative half of the teacher's door. Signing out is not the same fixture: a
   * signed-out viewer and a signed-in learner are two different reasons to be shown
   * nothing, and the rule being tested is a DID comparison — so the case worth
   * measuring is a real account that simply is not the one holding the book.
   */
  otherViewer: boolean
  /**
   * Keep the reader's place without seeding one: the second visit, in a browser.
   *
   * Paired with `progress` across a reload — the first load seeds a history and
   * continues from it, the second load carries no seed at all and must therefore
   * be reading what the first visit *saved*.
   */
  track: boolean
  /**
   * Show the shelf of books part-way through, over a device that has several.
   *
   * The shelf's content is *device state* and nothing else — several books, each at
   * its own page and each read at its own time — so there is no record to read it
   * from and a browser cannot produce the index by playing anything: a shelf with
   * one book cannot show an order, and an order is half of what it is for. Seeded
   * with three books part-way through and one that has been *finished*, which is
   * the half of the rule a list of books would otherwise get wrong.
   */
  shelf: boolean
  /**
   * Put a reading place in the learner's *repo*, as if they had been reading this
   * book on another device.
   *
   * The other half of "the place follows the learner": a fetch needs a record to
   * find, and a browser has no PDS to have written one — so the adopt path, and
   * the choice of which copy wins when the two disagree, would be unreachable. It
   * is two pages in, against `progress`'s three, and it is a read of its own with
   * its own id, so pairing the two is a genuine disagreement rather than a
   * coincidence. Pair with `session` — a record nobody is signed in to read is a
   * record the app rightly never asks for.
   */
  remote: boolean
  /**
   * Stand up the agent **signed out** — an account-less learner.
   *
   * The shape the app actually has when nobody is signed in: an agent that exists
   * and has no session, rather than no agent at all. A surface that read anything
   * but the session would look signed in here, and the promise being measured —
   * that a learner with no account is never asked for a record and never writes one
   * — could pass while being false on a device.
   */
  anonymous: boolean
  /**
   * Refuse every write of the reading *place*, as a repo that cannot be reached.
   *
   * The failure the clear-on-finish rule has to survive: the place never lands, so
   * the record stays whatever the learner's *other* device left there — and a
   * finish that only cleared a record it had written itself would leave that place
   * for the next device to offer. Pair with `progress` and `remote`, which is a
   * device copy and a repo copy that disagree, so there is something to refuse.
   */
  writeFail: boolean
  /**
   * Read the Arabic book instead: a whole book *written* right-to-left, so the
   * direction a page is laid out in is a property of the book rather than of one
   * mixed paragraph in a stress page.
   */
  lang: 'ar' | undefined
  /**
   * Put a recorded reading on the book's reading blocks.
   *
   * A block card is a bounded editor region with the block's audio band under it,
   * and a card that has a recording is the one that overflows that bound — so
   * without this every card the suite measures is a card that happens to fit.
   *
   * `'broken'` puts a take on them whose host does not resolve, which is the
   * other half of the audio desk: the clip it cannot *load*, as opposed to the
   * clip it cannot decode.
   *
   * `'levels'` puts a *real, decodable* reading on each of them, alternating
   * between loud and quiet, which is the book the level matcher exists for: one
   * that sounds like it was recorded in two different rooms.
   */
  audio: boolean | 'broken' | 'levels'
  /**
   * Open the studio on its *first* screen instead of a seeded book.
   *
   * Every other studio fixture starts in the page editor, because that is where
   * the block cards are; the cover step is the one screen with no fixture above
   * it, and a creator's very first screen is worth being able to look at (and
   * measure) rather than only describe.
   */
  step?: 'cover'
  /**
   * Stand up a fake signed-in creator for the studio.
   *
   * The studio reads its agent from `#/state/session`, and the drafts flow
   * fetches a record through it — `com.atproto.repo.getRecord`. A fresh browser
   * has no session, so without this the drafts list could be shown here but never
   * *opened*: a list you cannot open is not the feature, and the wait that follows
   * a tap could not be measured at all.
   */
  session: boolean
  /**
   * Open the studio's cover step with a film the picker would have returned, of
   * this length in ms — which is the only way a browser can reach the trimmer.
   */
  pickedFilmMs?: number
  /**
   * The *still* a fixture film cover carries, when the post fixture wants one.
   *
   * `record` — a book published with the frame the publisher derives from the film.
   * `none` — a book published before that field existed, whose only picture of
   * itself is the film, so the card has to read the frame for itself.
   */
  poster?: 'record' | 'none'
  /**
   * Read a book whose cover is a *film*, opening at `coverStartMs`.
   *
   * The reader's cover-film path is the one piece of the cover with no surface a
   * browser could reach: the seeded book wears a still image, so how a film cover
   * behaves for the learner — which minute it plays, whether it makes a sound, and
   * whether it stops when the book is left — could only be described. Set this and
   * the book opens on a real film instead.
   */
  cover?: 'video'
  /** Which minute of that film the book opens on. */
  coverStartMs?: number
  /**
   * Drop the *second* half of the fixture publish, keeping the first.
   *
   * Publishing a book is two writes: the post (which says `type: 'flashbook'`),
   * then the same post again carrying the book's back-ref and its counts. This is
   * the state a failed second write leaves behind — the post knows it is a book
   * and does not know which one — so the card has to find the record through its
   * author. It is the case a creator cannot tell apart from "it never published".
   */
  noRef?: boolean
}

const BLOCK_PARAM = 'block'

/**
 * The creator this fixture signs in as. `did:plc:` + 24 characters, because the
 * studio parses it as an AT-URI hostname rather than trusting it.
 */
const FIXTURE_DID = 'did:plc:abcdefghijklmnopqrstuvwx'

/**
 * A signed-in account that is not the creator's — the other side of every rule
 * that asks "is this the book's author?". Same 24-character shape as the creator's,
 * so nothing that parses a DID can tell the two apart by shape alone.
 */
const FIXTURE_OTHER_DID = 'did:plc:zyxwvutsrqponmlkjihgfedc'

/**
 * One delete, as the fixture agent saw it.
 *
 * Declared as a plain shape rather than as the SDK's parameters so the browser
 * can read it back: what matters is the *addressing* — which repo, which
 * collection, which record — because that is what "deleted on every device"
 * actually means at the wire.
 */
type FixtureDelete = {repo: string; collection: string; rkey: string}

/**
 * The knobs and readings a test sets on `window`.
 *
 * A fetch is otherwise either too fast to see or impossible to fail, and a
 * delete is otherwise invisible: the studio removes the row from its own list
 * either way, so a test could not tell a record actually deleted at the PDS
 * from one merely hidden here.
 */
/**
 * The fixture book, as a published record.
 *
 * Shaped exactly as the publisher writes it — the same field names, the same
 * counts — because the card reads it the way it reads a real one, and a fixture
 * that were tidy instead of faithful would test a reader that does not exist.
 */
function fixtureBookRecord(themeId?: string, model?: FlashBookModel) {
  const book = model ?? buildFlashBookFixture(themeId)
  return {
    $type: 'app.bsky.feed.flashbook',
    title: book.title,
    description: book.description,
    status: 'published',
    themeId: book.themeId,
    cover: book.cover,
    pages: book.pages,
    pageCount: book.pages.length,
    // Counted the publisher's own way, so the card's number and a real book's
    // number come from the same rule rather than from two fixtures' arithmetic.
    exerciseCount: countExercises(book.pages),
    subject: 'Maths',
    level: 'Level_B1',
    createdAt: '2026-09-01T10:00:00.000Z',
  }
}

/** The record key the fixture's published book lives at. */
const FIXTURE_BOOK_RKEY = 'book1'

/** The AT-URI that rkey resolves to, which is what the card opens. */
const FIXTURE_BOOK_URI = `at://${FIXTURE_DID}/app.bsky.feed.flashbook/${FIXTURE_BOOK_RKEY}`

/** The post it was published as: what the feed would be carrying. */
const FIXTURE_POST_URI = `at://${FIXTURE_DID}/app.bsky.feed.post/post1`

/**
 * The books on the shelf fixture's device, and the order they must appear in.
 *
 * Four decisions are baked into this list, and each is one the shelf can get wrong:
 *
 *  - **Distinct titles**, so a card that showed the wrong book is visible rather
 *    than plausible.
 *  - **Descending recency** — five minutes, an hour and a half, then yesterday — so
 *    the order on screen is a claim rather than an accident of insertion, and the
 *    list is written here in *reading* order while the map below is written out of
 *    it, so a shelf that followed insertion order would fail.
 *  - **A page each**, so "how far in" is per book rather than one number repeated.
 *  - **A theme each**, so the covers are drawn from three different token sets — the
 *    one part of a shelf card that can be silently wrong on every theme but one.
 */
const FIXTURE_SHELF_BOOKS = [
  {
    rkey: 'shelf-it-1',
    title: 'Second helpings',
    themeId: 'notebook',
    pageIndex: 2,
    minutesAgo: 5,
    finished: false,
  },
  {
    rkey: 'shelf-it-2',
    title: 'Le système solaire',
    themeId: 'space-adventure',
    pageIndex: 1,
    minutesAgo: 90,
    finished: false,
  },
  {
    rkey: 'shelf-it-3',
    title: 'The water cycle',
    themeId: 'laboratory',
    pageIndex: 0,
    minutesAgo: 60 * 26,
    finished: false,
  },
  /**
   * The book they *finished*, which must not be on the shelf at all.
   *
   * Its read most recent of all four, so a shelf that ordered by anything other
   * than "is there a read still open" would put it first — the loudest possible
   * place for the mistake.
   */
  {
    rkey: 'shelf-it-4',
    title: 'Already finished',
    themeId: 'academy',
    pageIndex: 3,
    minutesAgo: 1,
    finished: true,
  },
] as const

/** Where the shelf fixture's books live. */
const FIXTURE_SHELF_URI = (rkey: string) =>
  `at://${FIXTURE_DID}/app.bsky.feed.flashbook/${rkey}`

/** The shelf book at an rkey, when the rkey is one of them. */
function fixtureShelfBook(rkey: string) {
  return FIXTURE_SHELF_BOOKS.find(book => book.rkey === rkey)
}

/**
 * The blob ref the fixture agent answers every upload with.
 *
 * The URL a record ends up carrying is built from it (see `resolveBlobUrl` in
 * `#/components/flashbooks/flashbookSave`), so a
 * test can say which blob a record's cover points at — and, more usefully, that it
 * points at a *blob* rather than at the device-local URI it was made from.
 */
const FIXTURE_BLOB_CID = 'bafyfixtureblob'

/**
 * The feed row's post, as the publisher writes it.
 *
 * `noRef` drops the marker *and* the back-ref, which is the state a publish is
 * left in when its second write fails: the post exists, and nothing on it says it
 * is a book. The card is the only thing that can still find the record.
 *
 * The *book* is handed in rather than rebuilt here, because the post and the record
 * are two halves of one publish and the card reads both: a fixture that built its own
 * copy would let the two disagree — a post announcing a film cover above a record
 * holding a still one — which is a shape no publisher can produce, and the card
 * would be measuring a book that does not exist. The post is patched the way the
 * publisher patches it, so both halves hold what they really hold.
 */
function fixturePost(book: FlashBookModel, noRef: boolean) {
  const record = {
    // The first write: the marker, and the book's own words. Always present —
    // written by the publisher's own function so the two cannot drift.
    ...flashbookPostRecord({
      text: `${book.title}\n\n${book.description}`,
      createdAt: '2026-09-01T10:00:00.000Z',
    }),
    // The second write: everything that says *which* book it is. Dropped by
    // `noRef`, which is what a failed patch looks like from the feed.
    ...(noRef
      ? {}
      : flashbookPostFacts({
          bookUri: FIXTURE_BOOK_URI,
          bookCid: 'bafyfixturebook',
          pageCount: book.pages.length,
          exerciseCount: countExercises(book.pages),
          themeId: book.themeId,
          cover: book.cover,
        })),
  }
  return {
    $type: 'app.bsky.feed.defs#postView' as const,
    uri: FIXTURE_POST_URI,
    cid: 'bafyfixturepost',
    author: {
      did: FIXTURE_DID,
      handle: 'fixture-creator.test',
      displayName: 'Fixture Creator',
    },
    record,
    embed: undefined,
    labels: [],
    indexedAt: '2026-09-01T10:00:00.000Z',
    likeCount: 0,
    replyCount: 0,
    repostCount: 0,
    quoteCount: 0,
    bookmarkCount: 0,
  } as unknown as AppBskyFeedDefs.PostView
}

/**
 * A moderation context for the fixture row.
 *
 * The feed's own provider reads preferences over the network, which a signed-out
 * browser cannot do — and a row that never renders is not a row that can be
 * measured. So the override context the devtools use is the honest way in: the
 * real `PostFeedItem` renders against real moderation decisions, over a context
 * that says "this viewer has no preferences", which is true of this browser.
 */
const FIXTURE_MODERATION_OPTS: ModerationOpts = {
  userDid: undefined,
  prefs: {
    adultContentEnabled: false,
    labels: {},
    labelers: [],
    mutedWords: [],
    hiddenPosts: [],
  },
  labelDefs: {},
}

/**
 * A write the stand-in agent was asked to make.
 *
 * The *record* is carried, not only the collection: "a draft was saved" is a much
 * weaker claim than "these words are in the draft", and the second is the one that
 * matters to a creator who typed them.
 */
type FixtureWrite = {
  collection?: string
  rkey?: string
  value?: {
    status?: string
    title?: string
    description?: string
    /** A post's own words, for a *shared report* rather than a book. */
    text?: string
    /** The post's marker — absent on a report, which is deliberately a picture post. */
    type?: string
    embed?: {
      images?: {
        alt?: string
        aspectRatio?: {width?: number; height?: number}
      }[]
    }
  }
}

/**
 * A blob the stand-in PDS was asked to keep.
 *
 * The *size* is the point. A shared report is a generated picture, and the one
 * claim nobody can make by looking at the screen is that the picture posted is a
 * real image, inside the server's limit — so the fixture measures what it was
 * handed and writes it down.
 */
type FixtureUpload = {
  size: number
  type: string
  /** The picture's own pixels, when the browser could decode them. */
  width: number | null
  height: number | null
  /**
   * How much of it is *marks* rather than bare sheet, 0…1.
   *
   * A generated picture can decode perfectly and still be nothing: a white
   * rectangle, a card whose words did not make it into the capture, a snapshot
   * taken before the view had painted. So the bytes are read as pixels and the
   * share of them that is not the sheet's own colour is written down — a number a
   * test can hold to an opinion (`a poster is not a blank page`) that no
   * measurement of width and height could make.
   */
  ink: number
  /**
   * How much of the picture's *outer frame* is ink, 0…1.
   *
   * The other half of "it is a poster": a card keeps a wide margin inside its own
   * edge, so a picture whose border is smeared with marks has been **cropped** —
   * the failure a snapshotter has when it makes its canvas from a transformed
   * ancestor's bounds, and one no measurement of width and height can show.
   */
  edgeInk: number
}

type FixtureKnobs = {
  __flashbookDraftFetchMs?: number
  __flashbookDraftFetchFails?: boolean
  __flashbookDeleteFails?: boolean
  /**
   * Refuses a write of the *reading place*, and only that.
   *
   * Letting a draft save through is the point: this models the one failure the
   * clear-on-finish rule has to survive, which is a place that could not be
   * written — a lost connection, a PDS refusing — while the learner carries on
   * reading and then finishes the book. Set for no other collection, so a test
   * that leaves it on still measures the studio's saves.
   */
  __flashbookProgressWriteFails?: boolean
  /** Every `repo.deleteRecord` this agent has been asked for, in order. */
  __flashbookDeletedRecords?: FixtureDelete[]
  /**
   * Every `repo.createRecord` / `repo.putRecord` this agent was asked for.
   *
   * Writing them down rather than answering a fixed uri is what makes "saving a
   * draft really wrote one" a claim a test can read back — and it is the capsule
   * studio's Save-as-draft path that needs it, because a save is the one flow whose
   * only other evidence is a toast.
   */
  __fixtureCreatedRecords?: FixtureWrite[]
  /**
   * The reading place the learner's repo holds, for `remote=1`.
   *
   * Set by the fixture rather than by the test, because the record has to be the
   * app's *own* shape: one the app wrote is fetched back through the real reader,
   * so a test that hand-rolled the JSON could pass while the round trip was broken.
   */
  __flashbookRemoteProgress?: unknown
  /**
   * Every `repo.getRecord` this agent was asked for, in order.
   *
   * Recorded for the same reason the writes are: the promise the device store makes
   * is partly about what the app *does not* do — a signed-out learner is never asked
   * for a record — and an absence can only be asserted against a list that would
   * have held it.
   */
  __fixtureRecordReads?: {collection?: string; rkey: string}[]
  /** Every `uploadBlob` this agent has been asked for, with what it was given. */
  __fixtureUploads?: FixtureUpload[]
}

/**
 * A blob, as a picture: its own pixel size, or nulls when it will not decode.
 *
 * Read through an `<img>` rather than trusted from the upload call, because the
 * interesting failure is not "no blob" — it is a blob whose bytes are not the
 * picture anybody asked for.
 */
async function measureUploaded(blob: Blob | undefined): Promise<{
  width: number | null
  height: number | null
  ink: number
  edgeInk: number
}> {
  if (!blob || typeof document === 'undefined') {
    return {width: null, height: null, ink: 0, edgeInk: 0}
  }
  const url = URL.createObjectURL(blob)
  try {
    return await new Promise(resolve => {
      const image = new Image()
      image.onload = () =>
        resolve({
          width: image.naturalWidth,
          height: image.naturalHeight,
          ...measureInk(image),
        })
      image.onerror = () =>
        resolve({width: null, height: null, ink: 0, edgeInk: 0})
      image.src = url
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * The share of a picture that is not the colour most of it is.
 *
 * Sampled on a small canvas rather than read pixel by pixel at full size: the
 * question is whether the picture has anything *on* it, and a 40×50 thumbnail
 * answers that as well as a megapixel decode while costing nothing. Colours are
 * bucketed coarsely first, so the sheet's own paper — one flat colour with a
 * hairline grain — reads as one colour rather than as thousands.
 */
function measureInk(image: HTMLImageElement): {ink: number; edgeInk: number} {
  const w = 40
  const h = 50
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return {ink: 0, edgeInk: 0}
  ctx.drawImage(image, 0, 0, w, h)

  const {data} = ctx.getImageData(0, 0, w, h)
  const counts = new Map<string, number>()
  // Four bits per channel: enough to tell ink from paper, coarse enough that a
  // page's own texture is not mistaken for writing on it.
  const keyAt = (x: number, y: number) => {
    const i = (y * w + x) * 4
    return `${data[i] >> 4},${data[i + 1] >> 4},${data[i + 2] >> 4}`
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const key = keyAt(x, y)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
  }
  const total = w * h
  const dominantKey = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0]

  // The border, two samples deep: a card's own margin is far wider than that, so
  // this is paper on any picture that was not cut into.
  const frame: string[] = []
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (x < 2 || y < 2 || x >= w - 2 || y >= h - 2) {
        frame.push(keyAt(x, y))
      }
    }
  }
  return {
    ink: (total - counts.get(dominantKey)!) / total,
    edgeInk: frame.filter(k => k !== dominantKey).length / frame.length,
  }
}

/**
 * A capsule draft, as the drafts list's Continue fetch gets it.
 *
 * Shaped like the record the studio saves — title and description as their own
 * fields, the content slides in an array — because the studio reads a capsule draft
 * exactly the way it reads a real one.
 */
function fixtureCapsuleRecord(rkey: string) {
  return {
    $type: 'app.bsky.feed.capsule',
    title: `Opened ${rkey}`,
    description: 'Fetched by the fixture agent',
    status: 'draft',
    themeId: 'classic-quiz',
    slides: [
      {
        id: `fixture-${rkey}-1`,
        type: 'content',
        contentTitle: 'Warm up',
        contentText: 'Body',
      },
      {
        id: `fixture-${rkey}-2`,
        type: 'single_choice',
        question: 'Which one?',
        options: [],
      },
    ],
    slideCount: 1,
    createdAt: '2026-09-01T10:00:00.000Z',
  }
}

/**
 * A stand-in for the signed-in agent, with the two working methods the drafts
 * list needs.
 *
 * `repo.getRecord` returns a record built from the rkey that was asked for, so a
 * test can prove the tap opened *that* draft rather than merely that some book
 * appeared. `repo.deleteRecord` writes down what it was asked to delete instead
 * of answering: the studio hides the row from its own list whatever happens, so
 * the *only* way to prove the deletion left this device is to read the call back.
 *
 * Three knobs the test sets on `window`:
 *
 *  - `__flashbookDraftFetchMs` holds the answer open, so the row's spinner can be
 *    watched instead of raced;
 *  - `__flashbookDraftFetchFails` makes the fetch fail, which is the one way to
 *    reach the studio's *could not open this draft* path;
 *  - `__flashbookDeleteFails` makes the delete fail, which is how the list's
 *    promise to *keep* a draft it could not delete becomes measurable.
 */
/**
 * The stand-in agent, which answers with the *fixture's own* book.
 *
 * `themeId` is a dependency rather than a constant on purpose: the record is what
 * the card reads its theme and its counts from, so an agent that always answered
 * with the default theme would quietly make every themed fixture the same book —
 * nine runs of a theme sweep measuring storybook, and reporting success.
 *
 * `model` is the same book the post fixture is describing, when there is one. The
 * record and the post are the two halves of one publish, and a card reads both, so
 * an agent that answered with a *different* book would hand the card a cover its
 * post never mentioned — a shape no publisher produces.
 */
function useFixtureAgent(
  themeId?: string,
  model?: FlashBookModel,
  signedIn = true,
  /**
   * Whose session to serve. The creator by default; somebody else for the fixtures
   * that measure what a *non*-author sees.
   */
  did: string = FIXTURE_DID,
): AtpAgent {
  return useMemo(
    () =>
      ({
        /*
         * A learner with no account, when asked for. Not the same fixture as "no
         * agent at all": the app always has an agent, and a signed-out one has a
         * `session` of `null` — so a surface that reached for anything other than
         * the session would look signed in here and misbehave on a device.
         */
        session: signedIn ? {did, accessJwt: 'fixture'} : null,
        /**
         * The blob upload every save makes of a book's media.
         *
         * On the *root* rather than under `com.atproto.repo`, because that is the
         * call the app makes: `uploadBlob` in `#/lib/api` reaches for
         * `agent.uploadBlob`, the SDK's own convenience method. A fixture that put it
         * where the lexicon says it lives would answer the wrong object and leave a
         * save failing with `undefined is not a function`.
         *
         * It matters because a save *uploads first*: the fixture book's own cover is a
         * `data:` URI, the pipeline uploads it before writing the record, and without
         * this the studio's own Save — the flow whose only other evidence is a
         * toast — could not be driven here at all.
         *
         * It answers with a *stable* blob ref rather than a fresh one, which is what a
         * real PDS does with identical bytes, so a record written twice during one
         * session points at one picture.
         */
        uploadBlob: async (blob?: Blob) => {
          const knobs = globalThis as FixtureKnobs
          const measured = await measureUploaded(blob)
          ;(knobs.__fixtureUploads ??= []).push({
            size: blob?.size ?? 0,
            type: blob?.type ?? '',
            ...measured,
          })
          return {
            data: {
              blob: {
                ref: {toString: () => FIXTURE_BLOB_CID},
                mimeType: blob?.type || 'image/jpeg',
                size: blob?.size ?? 1024,
              },
            },
          }
        },
        com: {
          atproto: {
            repo: {
              /**
               * Whatever lives at the URI that was asked for: the fixture's
               * published book, or one of the studio's drafts.
               *
               * The *rkey* is what decides, because that is what decides on a real
               * PDS: publishing and drafting both write
               * `app.bsky.feed.flashbook`, so a collection check would answer every
               * draft read with the published book — which is not a fixture that
               * renders wrong, it is a fixture that makes two different surfaces
               * measure the same record.
               *
               * The knobs are applied *before* the choice, so the drafts list's
               * slow fetch and its refusal are still reachable — and still apply
               * to the record the caller asked for.
               */
              getRecord: async ({
                rkey,
                collection,
              }: {
                collection?: string
                rkey: string
              }) => {
                const knobs = globalThis as FixtureKnobs
                const wait = Number(knobs.__flashbookDraftFetchMs ?? 0)
                if (wait > 0) {
                  await new Promise(resolve => setTimeout(resolve, wait))
                }
                if (knobs.__flashbookDraftFetchFails) {
                  throw new Error('fixture: the record could not be fetched')
                }
                /*
                 * Written down before the answer, so a reader that is *refused* is
                 * still a reader the app made: "a signed-out learner asks for
                 * nothing" is a claim about the request, and a list of the ones
                 * that succeeded could not tell the two apart.
                 */
                ;(knobs.__fixtureRecordReads ??= []).push({collection, rkey})
                /*
                 * A reading place. The fixture either holds one — which is what
                 * "they were reading this on their other device" means, and the only
                 * way a browser can reach the adopt path at all — or it refuses the
                 * way a PDS does when there is nothing at that key. The refusal is
                 * thrown rather than returned empty on purpose: an app that treated
                 * an error as a record holding nothing would look identical here and
                 * would be wrong everywhere else.
                 */
                if (collection === PROGRESS_COLLECTION) {
                  const held = knobs.__flashbookRemoteProgress
                  if (!held) {
                    throw Object.assign(new Error('Could not locate record'), {
                      error: 'RecordNotFound',
                      status: 404,
                    })
                  }
                  return {
                    data: {
                      uri: `at://${FIXTURE_DID}/${PROGRESS_COLLECTION}/${rkey}`,
                      cid: 'bafyfixtureprogress',
                      value: held,
                    },
                  }
                }
                /*
                 * A capsule is recognised by its *collection*, which is not a
                 * shortcut: the two surfaces share this agent, and a capsule draft
                 * answered with the fixture's book would load an empty capsule
                 * under the book's title — a fixture that renders wrong rather than
                 * one that fails.
                 */
                if (collection === 'app.bsky.feed.capsule') {
                  return {
                    data: {
                      uri: `at://${FIXTURE_DID}/app.bsky.feed.capsule/${rkey}`,
                      cid: 'bafyfixturecapsule',
                      value: fixtureCapsuleRecord(rkey),
                    },
                  }
                }
                /*
                 * A book on the shelf. Answered per rkey rather than with the one
                 * fixture book, so each card's title, theme and place belong to
                 * *that* book — a shelf whose books all answered with the same
                 * record would show four identical covers and prove nothing about
                 * which one a tap opens.
                 */
                const shelfBook = fixtureShelfBook(rkey)
                if (shelfBook) {
                  const built = buildFlashBookFixture(shelfBook.themeId)
                  return {
                    data: {
                      uri: FIXTURE_SHELF_URI(rkey),
                      cid: `bafyfixtureshelf${rkey}`,
                      value: {
                        ...fixtureBookRecord(shelfBook.themeId),
                        // The book's own words, and its own identity: the reader
                        // opened *this* book, not the fixture's.
                        title: shelfBook.title,
                        id: FIXTURE_SHELF_URI(rkey),
                        pages: built.pages,
                        pageCount: built.pages.length,
                      },
                    },
                  }
                }
                if (rkey === FIXTURE_BOOK_RKEY) {
                  return {
                    data: {
                      uri: FIXTURE_BOOK_URI,
                      cid: 'bafyfixturebook',
                      value: fixtureBookRecord(themeId, model),
                    },
                  }
                }
                return {
                  data: {
                    value: {
                      title: `Opened ${rkey}`,
                      description: 'Fetched by the fixture agent',
                      themeId: 'notebook',
                      pages: [],
                      createdAt: '2026-09-01T10:00:00.000Z',
                    },
                  },
                }
              },
              /**
               * The author's own books, for a post that lost its back-ref.
               *
               * The card's slow path, and the one that answers the reported bug:
               * a book whose publisher never wrote the second half of its own
               * publish is still reachable through its author.
               */
              listRecords: async ({collection}: {collection?: string}) => {
                if (collection !== 'app.bsky.feed.flashbook') {
                  return {data: {records: []}}
                }
                return {
                  data: {
                    cursor: undefined,
                    records: [
                      {
                        uri: FIXTURE_BOOK_URI,
                        cid: 'bafyfixturebook',
                        value: {
                          ...fixtureBookRecord(themeId, model),
                          post: {
                            uri: `${FIXTURE_POST_URI}`,
                            cid: 'bafyfixturepost',
                          },
                        },
                      },
                    ],
                  },
                }
              },
              deleteRecord: async ({repo, collection, rkey}: FixtureDelete) => {
                const knobs = globalThis as FixtureKnobs
                /*
                 * Refuse *before* writing the call down, the way a real PDS
                 * does: a record that could not be deleted must not be
                 * remembered as deleted, or the reading below would report a
                 * successful delete for one that failed.
                 */
                if (knobs.__flashbookDeleteFails) {
                  throw new Error('fixture: the record could not be deleted')
                }
                ;(knobs.__flashbookDeletedRecords ??= []).push({
                  repo,
                  collection,
                  rkey,
                })
                return {data: {}}
              },
              /*
               * Saving a draft, which is the one write both studios make that is
               * not a publish. The uri is derived from a *fixed* rkey rather than a
               * fresh one, so a second save during one session updates the record
               * the first one made — the behaviour the studios promise, and one a
               * count of `createRecord` calls would otherwise hide.
               */
              createRecord: async ({
                collection,
                record,
              }: {
                collection?: string
                record?: FixtureWrite['value']
              }) => {
                const knobs = globalThis as FixtureKnobs
                const rkey = 'fixture-draft'
                ;(knobs.__fixtureCreatedRecords ??= []).push({
                  collection,
                  rkey,
                  value: record,
                })
                return {
                  data: {
                    uri: `at://${FIXTURE_DID}/${collection ?? ''}/${rkey}`,
                    cid: 'bafyfixturedraft',
                  },
                }
              },
              putRecord: async ({
                collection,
                rkey,
                record,
              }: {
                collection?: string
                rkey: string
                record?: FixtureWrite['value']
              }) => {
                const knobs = globalThis as FixtureKnobs
                /*
                 * A refused *place*, refused before it is written down — the
                 * same rule `deleteRecord` follows, and for the same reason: a
                 * write that failed must not be readable as one that landed, or
                 * a test could prove the repo agreed when it never answered.
                 */
                if (
                  knobs.__flashbookProgressWriteFails &&
                  collection === PROGRESS_COLLECTION
                ) {
                  throw new Error('fixture: the place could not be written')
                }
                ;(knobs.__fixtureCreatedRecords ??= []).push({
                  collection,
                  rkey,
                  value: record,
                })
                return {
                  data: {
                    uri: `at://${FIXTURE_DID}/${collection ?? ''}/${rkey}`,
                    cid: 'bafyfixturedraft',
                  },
                }
              },
            },
          },
        },
      }) as unknown as AtpAgent,
    // `did` is a dependency for the same reason the others are: the agent *is* the
    // session, so an agent memoised across a change of viewer would hand one fixture
    // another fixture's account.
    [themeId, model, signedIn, did],
  )
}

/**
 * Route params are the normal path, but a query string is what the tests can set
 * from a URL without wiring a deep link, so read both.
 */
function useFixtureParams(): FixtureParams {
  const route = useRoute()
  const params = (route.params ?? {}) as Partial<FixtureParams>

  return useMemo(() => {
    let fromQuery: {
      fixture?: FixtureParams['fixture']
      themeId?: string
      pageIndex?: string
      block?: string
      stress?: string
      lang?: string
      audio?: string
      step?: string
      session?: string
      pickedFilmMs?: string
      cover?: string
      coverStartMs?: string
      poster?: string
      noRef?: string
      report?: string
      progress?: string
      resume?: string
      track?: string
      remote?: string
      agent?: string
      writeFail?: string
      shelf?: string
      classPlays?: string
      classReport?: string
      viewer?: string
    } = {}
    if (typeof window !== 'undefined') {
      const search = new URLSearchParams(window.location.search)
      fromQuery = {
        fixture:
          (search.get('fixture') as FixtureParams['fixture']) ?? undefined,
        themeId: search.get('themeId') ?? undefined,
        pageIndex: search.get('pageIndex') ?? undefined,
        block: search.get(BLOCK_PARAM) ?? undefined,
        stress: search.get('stress') ?? undefined,
        lang: search.get('lang') ?? undefined,
        audio: search.get('audio') ?? undefined,
        step: search.get('step') ?? undefined,
        session: search.get('session') ?? undefined,
        pickedFilmMs: search.get('pickedFilmMs') ?? undefined,
        cover: search.get('cover') ?? undefined,
        coverStartMs: search.get('coverStartMs') ?? undefined,
        poster: search.get('poster') ?? undefined,
        noRef: search.get('noRef') ?? undefined,
        report: search.get('report') ?? undefined,
        progress: search.get('progress') ?? undefined,
        resume: search.get('resume') ?? undefined,
        track: search.get('track') ?? undefined,
        remote: search.get('remote') ?? undefined,
        agent: search.get('agent') ?? undefined,
        writeFail: search.get('writeFail') ?? undefined,
        shelf: search.get('shelf') ?? undefined,
        classPlays: search.get('class') ?? undefined,
        classReport: search.get('classReport') ?? undefined,
        viewer: search.get('viewer') ?? undefined,
      }
    }
    return {
      fixture: params.fixture ?? fromQuery.fixture ?? 'studio',
      themeId: params.themeId ?? fromQuery.themeId,
      report: !!fromQuery.report,
      pageIndex: Number(fromQuery.pageIndex) || 0,
      block: (fromQuery.block as FixtureBlockSpec | undefined) ?? undefined,
      stress: !!fromQuery.stress,
      progress: !!fromQuery.progress,
      resume: !!fromQuery.resume,
      track: !!fromQuery.track,
      remote: !!fromQuery.remote,
      anonymous: !!fromQuery.agent,
      writeFail: !!fromQuery.writeFail,
      shelf: !!fromQuery.shelf,
      classPlays: !!fromQuery.classPlays,
      classReport: !!fromQuery.classReport,
      otherViewer: fromQuery.viewer === 'other',
      lang: fromQuery.lang === 'ar' ? 'ar' : undefined,
      audio:
        fromQuery.audio === 'broken'
          ? 'broken'
          : fromQuery.audio === 'levels'
            ? 'levels'
            : !!fromQuery.audio,
      step: fromQuery.step === 'cover' ? 'cover' : undefined,
      session: !!fromQuery.session,
      pickedFilmMs: Number(fromQuery.pickedFilmMs) || undefined,
      cover: fromQuery.cover === 'video' ? 'video' : undefined,
      coverStartMs: Number(fromQuery.coverStartMs) || undefined,
      poster:
        fromQuery.poster === 'record' || fromQuery.poster === 'none'
          ? fromQuery.poster
          : undefined,
      noRef: !!fromQuery.noRef,
    }
  }, [params.fixture, params.themeId])
}

/**
 * A block switcher, so one page load can measure every block type. Reloading the
 * app for each of them dominated the layout suite's runtime.
 */
function BlockSwitcher({
  active,
  onSelect,
}: {
  active: FixtureBlockSpec | undefined
  onSelect: (type: FixtureBlockSpec | undefined) => void
}) {
  // It floats over either studio, or over the reader's book paper, so it wears
  // the chrome's canvas float: dark with white ink in both appearances, which is
  // the one treatment that is legible on any of them.
  const c = useStudioChrome()
  const options: (FixtureBlockSpec | undefined)[] = [
    undefined,
    ...FIXTURE_BLOCK_SPECS,
  ]
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={{maxHeight: 44, flexGrow: 0, backgroundColor: c.canvas}}
      contentContainerStyle={{alignItems: 'center', gap: 6, padding: 6}}>
      {options.map(type => (
        <TouchableOpacity
          key={type ?? 'page'}
          testID={`fixture-block-${type ?? 'page'}`}
          accessibilityRole="button"
          accessibilityLabel={type ?? 'whole page'}
          accessibilityHint="Shows this block in the player"
          onPress={() => onSelect(type)}
          style={{
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 12,
            backgroundColor: type === active ? c.accentFill : c.fillStrong,
            borderWidth: 1,
            borderColor: type === active ? c.accentBorder : c.canvasBorder,
          }}>
          <Text style={{color: c.onCanvas, fontSize: 11}}>
            {type ?? 'page'}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  )
}

export function FlashBookFixturesScreen() {
  const navigation = useNavigation()
  const {
    fixture,
    themeId,
    report,
    pageIndex,
    block,
    stress,
    lang,
    audio,
    step,
    resume,
    session,
    pickedFilmMs,
    cover,
    coverStartMs,
    poster,
    noRef,
    progress,
    track,
    remote,
    anonymous,
    writeFail,
    shelf,
    classPlays,
    classReport,
    otherViewer,
  } = useFixtureParams()
  const [blockType, setBlockType] = useState<FixtureBlockSpec | undefined>(
    block,
  )
  /**
   * Whether the reader was closed.
   *
   * A fixture is a surface rather than a route, so "leaving the book" has nowhere
   * to go and would be invisible: a class report opened straight from a card has to
   * be able to prove that its way out *leaves*, as opposed to dropping the teacher
   * into a report of a read they never did. So closing the reader is drawn, and the
   * screen says so.
   */
  const [leftBook, setLeftBook] = useState(false)

  const book = useMemo(() => {
    const built = stress
      ? buildStressFixture(themeId)
      : blockType
        ? buildSingleBlockFixture(blockType, themeId)
        : lang === 'ar'
          ? buildArabicFixture(themeId)
          : buildFlashBookFixture(themeId)
    if (audio === 'levels') return withLevelledReadings(built)
    const read = audio
      ? withReadingAudio(
          built,
          audio === 'broken' ? FIXTURE_UNREADABLE_AUDIO : undefined,
        )
      : built
    return cover === 'video' ? withFilmCover(read, coverStartMs) : read
  }, [blockType, stress, lang, themeId, audio, cover, coverStartMs])

  /*
   * The book the *post* fixture publishes: a film cover whose still may or may not
   * be in the record, which is the pair of books this fixture exists to tell apart.
   *
   * The agent serves this same model, because the post and the record are halves of
   * one publish — see `fixturePost`.
   */
  const postBook = useMemo(
    () =>
      poster
        ? withFilmCover(
            book,
            coverStartMs ?? 0,
            poster === 'record' ? FIXTURE_COVER_POSTER_URI : undefined,
          )
        : book,
    [book, coverStartMs, poster],
  )
  /*
   * The fixture's own book stays out of the agent everywhere but here, and that is
   * deliberate: the other fixtures build their book *after* picking a block, and an
   * agent whose identity changed under them would be a fixture that re-renders
   * whatever subscribes to it every time the block switcher is used.
   */
  const agent = useFixtureAgent(
    themeId,
    fixture === 'post' ? postBook : undefined,
    !anonymous,
    // A signed-in account that is *not* the book's creator, for the negative half
    // of every author-gated door. One DID comparison is the whole rule, so the
    // case that has to be measured is a real session on the wrong side of it.
    otherViewer ? FIXTURE_OTHER_DID : FIXTURE_DID,
  )

  /*
   * The read the report is opened on.
   *
   * Built from the book itself, so the report names the fixture's own questions
   * and answers rather than a second, test-only copy of them.
   */
  const seededRead = useMemo(
    () => (report ? buildReportSeed(book.pages) : undefined),
    [report, book.pages],
  )

  /*
   * The class's plays, built from this book.
   *
   * Only when the fixture asked: six learners' worth of results handed to a book
   * nobody played would be a fixture that lies about every other surface here.
   */
  const fixtureClassPlays = useMemo(
    () => (classPlays ? buildClassPlays(book.pages) : undefined),
    [classPlays, book.pages],
  )

  /*
   * A history for the book, put into device storage *during this render*.
   *
   * Deliberately not in an effect: the player reads its own place as it mounts,
   * and a parent's effect runs after its children have already mounted, so seeding
   * from an effect would have the reader open on the previous visit's history —
   * which on a first load is nothing at all. Written once per mount, so a
   * re-render cannot overwrite what the reader has since saved.
   */
  /*
   * Which name the seeded read belongs to.
   *
   * The player is handed the fixture *model* and keys a read by the model's own id,
   * while a feed card is handed a *record* and keys it by the record's URI — and in
   * this fixture those are two names for one book, because the record is served at
   * `FIXTURE_BOOK_URI` whatever the model calls itself. So the seed is written under
   * the name the surface in front of it will look up: a post fixture that seeded the
   * model's id would seed a place the card could never find, and the card would be
   * measured against a device that has never read the book.
   */
  const seededBookUri = fixture === 'post' ? FIXTURE_BOOK_URI : book.id
  const seededRegister = useMemo(
    () =>
      progress
        ? buildProgressSeed({
            bookUri: seededBookUri,
            bookTitle: book.title,
            pages: book.pages,
          })
        : null,
    [progress, seededBookUri, book.title, book.pages],
  )
  const seededStore = useRef<boolean | null>(null)
  if (seededStore.current == null) {
    seededStore.current = true
    if (seededRegister) putFlashBookProgress(seededRegister)
  }

  /*
   * The place on the learner's *other* device, as their repo holds it.
   *
   * Installed during render, once per mount, for the same reason the device seed
   * is: the reader fetches it as it mounts, and a record set from an effect would
   * land after the cover had already been drawn — and the cover is the screen this
   * claim is about. Built through the app's own record builder rather than as
   * hand-written JSON, so what the reader parses is exactly what a reader of a real
   * record would ever see.
   */
  const remoteSeed = useMemo(
    () =>
      remote
        ? progressRecordFor(
            buildRemoteReadSeed({
              bookUri: book.id,
              bookTitle: book.title,
              pages: book.pages,
            }),
          )
        : null,
    [remote, book.id, book.title, book.pages],
  )
  const remoteStore = useRef<boolean | null>(null)
  if (remoteStore.current == null) {
    remoteStore.current = true
    if (remoteSeed) {
      ;(globalThis as FixtureKnobs).__flashbookRemoteProgress = remoteSeed
    }
  }

  /*
   * The shelf's device state: several books, each at its own page and each read at
   * its own time.
   *
   * Installed during render, once per mount, for the same reason the other seeds
   * are — the shelf reads the index on its first frame, and a seed written from an
   * effect would land after the shelf had already drawn itself empty. Written
   * through the store's own writer, so the index the shelf reads is the shape the
   * reader writes rather than a fixture-only one.
   */
  const shelfSeed = useMemo(
    () =>
      shelf
        ? FIXTURE_SHELF_BOOKS.map(entry =>
            buildShelfRead({
              bookUri: FIXTURE_SHELF_URI(entry.rkey),
              bookTitle: entry.title,
              pages: buildFlashBookFixture(entry.themeId).pages,
              pageIndex: entry.pageIndex,
              lastReadAt: new Date(
                Date.now() - entry.minutesAgo * 60_000,
              ).toISOString(),
              finished: entry.finished,
            }),
          )
        : null,
    [shelf],
  )
  const shelfStore = useRef<boolean | null>(null)
  if (shelfStore.current == null) {
    shelfStore.current = true
    shelfSeed?.forEach(putFlashBookProgress)
  }

  /*
   * A repo that refuses the *place*, for the one failure the clear-on-finish rule
   * has to survive: a write that never landed, followed by a learner who reads on
   * and finishes. Installed during render like the seeds above, because the very
   * first write is the one being refused — a knob set from an effect would arrive
   * after the app had already been told its place had been written.
   */
  const writeFailStore = useRef<boolean | null>(null)
  if (writeFailStore.current == null) {
    writeFailStore.current = true
    ;(globalThis as FixtureKnobs).__flashbookProgressWriteFails = writeFail
  }

  if (fixture === 'capsule') {
    /*
     * The Capsule studio is a screen for the same reason the FlashBook studio
     * is: it reads `navigation` and `route.params` for its entry state.
     */
    const capsuleProps = {
      navigation,
      route: {
        key: 'capsule-fixture',
        name: 'CapsuleStudio',
        params: {themeId},
      },
    } as unknown as React.ComponentProps<typeof CapsuleStudioScreen>
    /*
     * Signed in only when asked for, exactly as the book studio is: the drafts flow
     * fetches a record through the agent, and a fresh browser has no session — so
     * without this the list could be shown here but never *opened*, and the leave
     * dialog's Save-as-draft could only ever end in an error toast.
     */
    return session ? (
      <AgentContext.Provider value={agent}>
        <CapsuleStudioScreen {...capsuleProps} />
      </AgentContext.Provider>
    ) : (
      <CapsuleStudioScreen {...capsuleProps} />
    )
  }

  /*
   * A published book, in the feed.
   *
   * This is the real feed row — `PostFeedItem` — not the card on its own, because
   * the bug this surface exists for was never in the card: it was that the feed
   * had no branch for a book post at all. A fixture that rendered the card
   * directly could not have caught that, and could not catch it coming back.
   */
  if (fixture === 'post') {
    const post = fixturePost(postBook, !!noRef)
    return (
      <moderationOptsOverrideContext.Provider value={FIXTURE_MODERATION_OPTS}>
        <AgentContext.Provider value={agent}>
          <Layout.Screen>
            <Layout.Center style={{flex: 1}}>
              <PostFeedItem
                post={post}
                record={post.record as AppBskyFeedPost.Record}
                reason={undefined}
                feedContext={undefined}
                reqId={undefined}
                moderation={moderatePost(post, FIXTURE_MODERATION_OPTS)}
                parentAuthor={undefined}
                showReplyTo={false}
                rootPost={post}
                hideTopBorder
              />
            </Layout.Center>
          </Layout.Screen>
        </AgentContext.Provider>
      </moderationOptsOverrideContext.Provider>
    )
  }

  /*
   * The shelf, on a device that has been reading several books.
   *
   * Rendered as the component rather than as the home feed, because the shelf's
   * whole subject is its own contents: a browser that had to reach it by scrolling
   * a feed could measure the same four facts only through a screen whose feed
   * queries have nothing to do with them.
   */
  if (fixture === 'shelf') {
    return (
      <AgentContext.Provider value={agent}>
        <Layout.Screen>
          <Layout.Center style={{flex: 1}}>
            <FlashBookShelf />
          </Layout.Center>
        </Layout.Screen>
      </AgentContext.Provider>
    )
  }

  if (fixture === 'player') {
    /*
     * Signed in only when asked for, exactly as the studio and capsule fixtures
     * are: sharing a report to the feed is a write, and a fresh browser has no
     * session — so without this the sheet could be shown here but never *posted*
     * from, which is the half of the feature worth measuring.
     */
    /*
     * Out of the book, drawn.
     *
     * The class report's way out when the reader was opened straight on it is the
     * way out of the book, and this fixture has nowhere for that to go — so closing
     * it is shown here rather than silently swallowed, which is the only way a
     * browser can tell "left the book" from "is still in it".
     */
    if (leftBook) {
      return (
        <View
          testID="flashbook-fixture-left"
          style={{flex: 1, alignItems: 'center', justifyContent: 'center'}}>
          <Text>Left the book.</Text>
        </View>
      )
    }

    const playerView = (
      <View style={{flex: 1}}>
        {/* Remount on switch: the player keeps its own page state. */}
        <FlashBookPlayer
          key={blockType ?? `page-${pageIndex}`}
          book={book}
          /* A film cover is only reachable by *opening the book*, so the film
             fixture stays on the cover rather than skipping to page one. */
          startInReading={progress || track ? false : cover !== 'video'}
          /* The way into a book that lands on the page, not the cover — asked for
             only when the fixture says so, because every other route here measures
             a screen whose first frame the cover *is*. */
          openAtPlace={resume}
          /*
           * The teacher's way in, asked for and then judged: `isAuthor` below is
           * the same flag the class's plays are gated on, so the pair of them is
           * the whole rule — the author gets the report, anybody else gets a book.
           */
          openAtClassReport={classReport}
          initialPageIndex={blockType ? 0 : pageIndex}
          seededRead={seededRead}
          /* The same history, handed to the ending as well as to the store. A
             report opened over a fixture read has no register of its own (a
             fixture read is never recorded), so this is what lets the browser
             measure the progression across reads rather than only the read. */
          seededProgress={report ? seededRegister : undefined}
          /*
           * The author, and their class. Passed together because they are the same
           * fact read twice: only the person who set the book has a class to report
           * on, so a fixture that had one without the other would be measuring a
           * screen no user can reach.
           */
          isAuthor={classPlays}
          classPlays={fixtureClassPlays}
          /*
           * Only when the fixture asked for it. Every other route here measures a
           * book on a learner's behalf, and one that quietly wrote a read would
           * leave a history behind that the next run's cover would offer to
           * continue — so a layout suite would start measuring a resume screen.
           */
          trackProgress={progress || track}
          onClose={() => {
            // Nowhere to close back to — but *that* is the fact one class-report
            // route is measured by, so it is drawn rather than dropped.
            setLeftBook(true)
          }}
        />
        {/* The switcher swaps in a one-block book, which would replace the very
            book the stress run is here to measure. */}
        {!stress &&
          !lang &&
          !report &&
          !progress &&
          !track &&
          !classPlays &&
          !classReport && (
            <View style={{position: 'absolute', top: 0, left: 0, right: 0}}>
              <BlockSwitcher active={blockType} onSelect={setBlockType} />
            </View>
          )}
      </View>
    )
    return session || anonymous ? (
      <AgentContext.Provider value={agent}>{playerView}</AgentContext.Provider>
    ) : (
      playerView
    )
  }

  /*
   * The studio is a screen, not a component: it reads `navigation` and
   * `route.params` for its entry state. Rendering it here with a synthesized
   * route is what lets a session-less browser reach the real thing — the casts
   * are the price of that, and they are confined to this file.
   */
  const studioProps = {
    navigation,
    route: {
      key: 'flashbook-fixture',
      name: 'FlashBookStudio',
      // No fixture book on the cover step: the screen opens on the same empty
      // book a creator meets, rather than a seeded one.
      params: {
        themeId,
        fixture: step === 'cover' ? undefined : 'layout',
        pageIndex,
        audio,
        pickedFilmMs,
      },
    },
  } as unknown as React.ComponentProps<typeof FlashBookStudioScreen>

  /*
   * Signed in only when asked for, and by a stand-in: every other surface here
   * is measured logged out, which is what the rest of this screen assumes. The
   * provider wraps the studio alone, so nothing else in the app can be affected
   * by a fixture.  */
  return session ? (
    <AgentContext.Provider value={agent}>
      <FlashBookStudioScreen {...studioProps} />
    </AgentContext.Provider>
  ) : (
    <FlashBookStudioScreen {...studioProps} />
  )
}
